import React, {useCallback, useMemo, useState} from 'react';
import {Autocomplete as MuiAutocomplete, Stack} from '@mui/material';
import type {AutocompleteRenderInputParams} from '@mui/material/Autocomplete';
import type {
  AutocompleteRenderValue,
  AutocompleteRenderValueGetItemProps,
} from '@mui/material/Autocomplete/Autocomplete';
import Chip from '@mui/material/Chip';
import type {CurrentFilterType} from 'services/advancedSearch/types';
import type {CodeListId} from 'services/codelists/types';
import {useCodelist} from 'services/codelists';
import {TextField} from 'components';
import type {AutocompleteOption} from '../../types';
import {canFreeSolo} from '../../functions';
import {mapCodelistToOptions} from '../../functions/mapCodelistToOptions';

type Props = {
  codelistId: CodeListId;
  filter: CurrentFilterType;
  onChange: (filter: CurrentFilterType) => void;
  onInputValueChange?: (value: string) => void;
};

export const FilterCodelistValue: React.FC<Props> = ({
  codelistId,
  filter,
  onChange,
  onInputValueChange,
}) => {
  const codelist = useCodelist(codelistId);

  const [inputValue, setInputValue] = useState<string>('');

  const handleInputChange = useCallback(
    (_: unknown, newValue: string) => {
      setInputValue(newValue);
      if (onInputValueChange) {
        onInputValueChange(newValue);
      }
    },
    [onInputValueChange],
  );

  const freeSolo = useMemo(() => {
    return canFreeSolo(filter);
  }, [filter]);

  const handleOnChange = useCallback(
    (
      _: React.SyntheticEvent,
      value:
        | AutocompleteOption
        | (AutocompleteOption | string)[]
        | string
        | null,
    ) => {
      const values = (
        value === null ? [] : Array.isArray(value) ? value : [value]
      )
        .filter(v => !(!freeSolo && typeof v === 'string'))
        .map(v => (typeof v === 'string' ? v : v.id));

      onChange({
        ...filter,
        values,
      });
    },
    [filter, freeSolo, onChange],
  );

  const options: AutocompleteOption[] = useMemo(() => {
    return mapCodelistToOptions(codelist);
  }, [codelist]);

  const values: AutocompleteOption[] = useMemo(() => {
    return (
      filter.values?.map(f => ({
        id: f,
        label: options?.find(c => c.id === f)?.label ?? f,
      })) ?? []
    );
  }, [filter.values, options]);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        type={'text'}
        name={filter.name}
        layout={{flex: 1}}
        {...params}
      />
    ),
    [filter.name],
  );

  const renderValue = useCallback(
    (
      value: AutocompleteRenderValue<AutocompleteOption, true, true>,
      getTagProps: AutocompleteRenderValueGetItemProps<true>,
    ) => {
      return (
        <Stack
          direction={'row'}
          flexWrap={'wrap'}
          gap={0.5}
          width={'inherit'}
          overflow={'hidden'}>
          {value
            .filter(s => typeof s !== 'string')
            .map((option: AutocompleteOption, index: number) => (
              <Chip
                label={option.label ?? option.id}
                size={'small'}
                {...getTagProps({index})}
                key={option.id}
              />
            ))}
        </Stack>
      );
    },
    [],
  );

  return (
    <MuiAutocomplete
      multiple={true}
      freeSolo={freeSolo}
      value={values}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={v => (typeof v === 'string' ? v : v.label)}
      onChange={handleOnChange}
      filterOptions={onInputValueChange ? x => x : undefined} // Tells MUI not to filter internally if we provide onInputValueChange
      renderValue={renderValue}
      noOptionsText={'Ingen treff'}
      size={'small'}
      renderInput={renderInput}
    />
  );
};
